import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Collapse, Drawer, Empty, Skeleton, Space, Tabs, Tooltip, Typography } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExperimentOutlined,
  InfoCircleOutlined,
  RobotOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import {
  getFindingReview,
  getResultEvidence,
  type FailureEvidence,
  type FindingReview,
  type TurnEvidence,
  type ValidationResultEvidence,
} from '@/api/validationRuns';
import { formatMs } from '@/api/runEvidence';
import { colors } from '@/theme/tokens';
import { StatusPill } from '@/components/StatusPill';
import { doubledCallWords } from '@/features/scenario-intelligence/scenarioWords';
import { FindingReviewPanel } from './FindingReviewPanel';
import { ObligationsPanel } from './ObligationsPanel';
import { DebuggerPanel, findingTurnOf } from './DebuggerPanel';

const { Text } = Typography;
const MONO = "'JetBrains Mono', monospace";

// One scenario result opened as its conversation (EVAL-06 STUDIO-EVAL-05a).
// Read-only. Answers, in order: what went wrong, what the record cannot show,
// and what happened turn by turn — the agent's words, the tools it called, and
// the skills the router supplied. Every note from the server is printed as
// given; a gap is never filled in ("not recorded" is not "did not happen").
// SI-03b: the header names what was tested (agent, candidate revision, the
// deployment that answered); "What went wrong" is every check as expected
// against observed; the Debugger tab reads a turn in the order it happened and
// opens on the turn the finding points at.

export const FindingDrawer = observer(function FindingDrawer({
  resultId,
  onClose,
}: {
  resultId: number | null;
  onClose: () => void;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [evidence, setEvidence] = useState<ValidationResultEvidence | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState<FindingReview | null | undefined>(undefined);

  useEffect(() => {
    if (resultId == null) return;
    const request = new AbortController();
    setEvidence(null);
    setError(null);
    setLoading(true);
    setReview(undefined);
    getResultEvidence(resultId, request.signal)
      .then((e) => {
        if (request.signal.aborted) return;
        setEvidence(e);
        // The finding's recorded cause, by a person: the only cause this drawer shows (no AI cause is suggested here).
        if (!e.passed || e.traceError)
          getFindingReview(resultId, request.signal)
            .then((r) => !request.signal.aborted && setReview(r))
            .catch(() => !request.signal.aborted && setReview(null));
      })
      .catch((err: Error) => {
        if (!request.signal.aborted) setError(err.message);
      })
      .finally(() => {
        if (!request.signal.aborted) setLoading(false);
      });
    return () => request.abort();
  }, [resultId]);

  return (
    <Drawer
      open={resultId != null}
      onClose={onClose}
      size="min(780px, 100vw)"
      destroyOnClose
      title={
        <div style={{ minWidth: 0 }}>
          <Space size={8} align="center" wrap>
            <ExperimentOutlined />
            <span style={{ fontFamily: MONO, wordBreak: 'break-all' }}>{evidence?.scenarioId ?? 'Scenario result'}</span>
            {evidence &&
              (evidence.passed ? (
                <StatusPill tone="green" icon={<CheckCircleFilled />}>Passed</StatusPill>
              ) : evidence.traceError && !(evidence.obligations?.obligations.some((o) => o.state === 'Fail') ?? evidence.failedAssertionCount > 0) ? (
                <StatusPill tone="slate">Not assessed</StatusPill>
              ) : (
                <StatusPill tone="red" icon={<CloseCircleFilled />}>Failed</StatusPill>
              ))}
          </Space>
          {evidence && <IdentityLine evidence={evidence} />}
        </div>
      }
      styles={{ body: { background: isDark ? colors.slate[900] : colors.slate[50] } }}
    >
      {loading && <Skeleton active paragraph={{ rows: 8 }} />}
      {error && <Alert type="error" showIcon title="Could not load this result" description={error} />}
      {evidence && <EvidenceTabs key={evidence.resultId} evidence={evidence} isDark={isDark} review={review} />}
    </Drawer>
  );
});

/** What was tested, in one line: the run, the agent and its revision, the deployment that answered (rounds by who). */
function IdentityLine({ evidence }: { evidence: ValidationResultEvidence }) {
  const id = evidence.identity;
  const calls = evidence.turns.flatMap((t) => t.runtime?.modelCalls ?? []);
  const rounds = (role: string) => calls.filter((c) => c.role === role).reduce((n, c) => n + Math.max(1, c.rounds?.length ?? 0), 0);
  const answered = [...new Set(calls.filter((c) => c.role === 'Agent').map((c) => c.model).filter(Boolean))];
  const agentRounds = rounds('Agent');
  const peerRounds = rounds('Peer');
  const parts = [
    `Run #${evidence.validationRunId}`,
    `${id?.agentCanonicalName ?? `agent #${evidence.agentId}`}${id?.candidateHash ? ` ${id.candidateHash.slice(0, 7)}` : ''}`,
    answered.length
      ? `answered by ${answered.join(', ')}${id?.apiTitle ? ` · ${id.apiTitle}` : ''} (${agentRounds} agent round${agentRounds === 1 ? '' : 's'}${peerRounds ? `, ${peerRounds} peer round${peerRounds === 1 ? '' : 's'}` : ''})`
      : 'which deployment answered is not recorded',
    'deterministic checks only',
  ];
  return (
    <div style={{ fontSize: 12, fontWeight: 400, color: colors.slate[500], marginTop: 2, whiteSpace: 'normal' }} data-testid="finding-identity">
      {parts.join(' · ')}
    </div>
  );
}

function EvidenceTabs({ evidence, isDark, review }: { evidence: ValidationResultEvidence; isDark: boolean; review: FindingReview | null | undefined }) {
  const [tab, setTab] = useState('conversation');
  const [debugTurn, setDebugTurn] = useState(() => findingTurnOf(evidence));
  const openTurn = (turnIndex: number) => {
    setDebugTurn(turnIndex);
    setTab('debugger');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ObligationsPanel evidence={evidence} review={review} isDark={isDark} onOpenTurn={openTurn} />

      {evidence.completeness.notes.length > 0 && (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          title="What this record cannot show"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {evidence.completeness.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          }
        />
      )}

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'conversation',
            label: `Conversation (${evidence.turns.length})`,
            children:
              evidence.turns.length === 0 ? (
                <Empty description="No turns were recorded for this result." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {evidence.turns.map((t) => (
                    <TurnCard key={t.turnIndex} turn={t} isDark={isDark} />
                  ))}
                </div>
              ),
          },
          // SI-03b: the turn in the order it happened, opening on the turn the finding points at.
          {
            key: 'debugger',
            label: 'Debugger',
            children: <DebuggerPanel evidence={evidence} turnIndex={debugTurn} onTurnChange={setDebugTurn} isDark={isDark} />,
          },
          // EVAL-06: a failed or incomplete result is a finding someone can own, classify, resolve or dispute.
          ...(!evidence.passed || evidence.traceError
            ? [{ key: 'review', label: 'Review', children: <FindingReviewPanel resultId={evidence.resultId} /> }]
            : []),
          {
            key: 'rules',
            label: `Conversation rules (${evidence.conversationRules.length})`,
            children:
              evidence.conversationRules.length === 0 ? (
                <Empty description="This scenario has no conversation-level rules." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {evidence.conversationRules.map((r) => (
                    <div key={r.rule} style={card(isDark, !r.passed)}>
                      <Space size={8}>
                        {r.passed ? (
                          <StatusPill tone="green">Passed</StatusPill>
                        ) : (
                          <StatusPill tone="red">Failed</StatusPill>
                        )}
                        <Text style={{ fontFamily: MONO, fontWeight: 600 }}>{r.rule}</Text>
                      </Space>
                      {r.failures.length > 0 && <FailureList failures={r.failures} isDark={isDark} />}
                    </div>
                  ))}
                </div>
              ),
          },
        ]}
      />
    </div>
  );
}

function TurnCard({ turn, isDark }: { turn: TurnEvidence; isDark: boolean }) {
  const failed = turn.assertionsPassed === false || !!turn.error;
  const status =
    turn.assertionsPassed === true ? (
      <StatusPill tone="green">Checks passed</StatusPill>
    ) : turn.assertionsPassed === false ? (
      <StatusPill tone="red">Checks failed</StatusPill>
    ) : (
      <StatusPill tone="slate">Not checked</StatusPill>
    );

  const details = [];
  if (turn.toolCalls.length > 0) {
    details.push({
      key: 'tools',
      label: (
        <Space size={6}>
          <ToolOutlined />
          {`Actions the agent took (${turn.toolCalls.length})`}
        </Space>
      ),
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {turn.toolCalls.map((c) => (
            <div key={c.order}>
              <Text style={{ fontFamily: MONO, fontWeight: 600 }}>{c.toolName}</Text>
              {c.durationMs != null && <Text type="secondary"> · {formatMs(c.durationMs)}</Text>}
              {c.outcome === 'NoResult' && (
                <>
                  {' '}
                  <StatusPill tone="red">No result</StatusPill>
                </>
              )}
              {c.outcome === 'Doubled' ? (
                <>
                  {' '}
                  <Tooltip title={`${doubledCallWords(c.label)}. The scenario says what this tool answers, so the tool did not run.`}>
                    <span>
                      <StatusPill tone="ink">Scenario’s double</StatusPill>
                    </span>
                  </Tooltip>
                </>
              ) : c.simulated ? (
                <>
                  {' '}
                  <Tooltip title="A test run does not carry out the agent's own tools that change a record or send something. The agent asked for this action; the run answered for the tool, and nothing was carried out. An agent it consults may still create a file, which stays unused in storage.">
                    <span>
                      <StatusPill tone="amber">Not carried out</StatusPill>
                    </span>
                  </Tooltip>
                </>
              ) : (
                c.label && <Text type="secondary"> · {c.label}</Text>
              )}
              <Payload title="Sent" body={c.request} cut={c.requestTruncated} isDark={isDark} />
              <Payload title="Returned" body={c.response} cut={c.responseTruncated} isDark={isDark} />
            </div>
          ))}
        </div>
      ),
    });
  }
  if (turn.router) {
    details.push({
      key: 'router',
      label: (
        <Space size={6}>
          <ExperimentOutlined />
          {`Skills the router supplied (${turn.router.skills.length})`}
        </Space>
      ),
      children: (
        <div>
          <Text type="secondary">
            Router {turn.router.status ?? 'status unknown'}
            {turn.router.model ? ` · ${turn.router.model}` : ''}
            {turn.router.failureReason ? ` · ${turn.router.failureReason}` : ''}
          </Text>
          {turn.router.skills.length === 0 ? (
            <div>
              <Text type="secondary">No skill was supplied by the router on this turn.</Text>
            </div>
          ) : (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {turn.router.skills.map((s) => (
                <li key={s.name}>
                  <Text style={{ fontFamily: MONO }}>{s.name}</Text>
                  {s.alwaysLoad && <Text type="secondary"> · always loaded</Text>}
                  {s.reason && <div style={{ color: colors.slate[500], fontSize: 12 }}>{s.reason}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ),
    });
  }

  return (
    <div style={card(isDark, failed)}>
      <Space size={8} style={{ marginBottom: 10 }}>
        <Text strong>Turn {turn.turnIndex + 1}</Text>
        {status}
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatElapsed(turn.elapsedMs)}
        </Text>
      </Space>
      <Bubble who="Patient" icon={<UserOutlined />} text={turn.userMessage} isDark={isDark} side="left" />
      <Bubble who="Agent" icon={<RobotOutlined />} text={turn.agentReply} isDark={isDark} side="right" />
      {turn.error && (
        <Alert type="error" showIcon style={{ marginTop: 8 }} title="This turn did not complete" description={turn.error} />
      )}
      {turn.failures.length > 0 && <FailureList failures={turn.failures} isDark={isDark} />}
      {details.length > 0 && <Collapse size="small" ghost style={{ marginTop: 8 }} items={details} />}
      {turn.toolCalls.length === 0 && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
          No tool call was recorded on this turn.
        </Text>
      )}
    </div>
  );
}

function Bubble({
  who,
  icon,
  text,
  isDark,
  side,
}: {
  who: string;
  icon: React.ReactNode;
  text?: string | null;
  isDark: boolean;
  side: 'left' | 'right';
}) {
  const mine = side === 'right';
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
      <div
        style={{
          maxWidth: '85%',
          padding: '10px 12px',
          borderRadius: 12,
          background: mine
            ? isDark ? `${colors.ink[700]}55` : colors.ink[50]
            : isDark ? colors.slate[800] : '#FFFFFF',
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.slate[500], marginBottom: 3 }}>
          {icon} {who}
        </div>
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: 13.5, color: isDark ? colors.slate[100] : colors.slate[800] }}>
          {text ? text : <span style={{ fontStyle: 'italic', opacity: 0.6 }}>(nothing recorded)</span>}
        </div>
      </div>
    </div>
  );
}

function FailureList({ failures, isDark }: { failures: FailureEvidence[]; isDark: boolean }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: '8px 10px',
        borderRadius: 8,
        background: isDark ? `${colors.red[700]}33` : colors.red[100],
        border: `1px solid ${colors.red[600]}55`,
      }}
    >
      {failures.map((f, i) => (
        <div key={i} style={{ marginBottom: i < failures.length - 1 ? 6 : 0 }}>
          <Text style={{ fontFamily: MONO, fontWeight: 600, color: isDark ? colors.red[100] : colors.red[700] }}>
            {f.check}
          </Text>
          <div style={{ color: isDark ? colors.red[100] : colors.red[700] }}>{f.detail}</div>
        </div>
      ))}
    </div>
  );
}

function Payload({ title, body, cut, isDark }: { title: string; body?: string | null; cut: boolean; isDark: boolean }) {
  return (
    <div style={{ marginTop: 4 }}>
      <Text type="secondary" style={{ fontSize: 11.5 }}>
        {title}
        {cut ? ' (cut when stored)' : ''}
      </Text>
      <pre
        style={{
          margin: '2px 0 0',
          padding: 8,
          maxHeight: 180,
          overflow: 'auto',
          fontFamily: MONO,
          fontSize: 11.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          borderRadius: 6,
          background: isDark ? colors.slate[800] : colors.slate[100],
        }}
      >
        {body ? body : '(nothing recorded)'}
      </pre>
    </div>
  );
}

function card(isDark: boolean, failed: boolean): React.CSSProperties {
  return {
    padding: '12px 14px',
    borderRadius: 12,
    background: isDark ? colors.slate[800] : '#FFFFFF',
    border: `1px solid ${failed ? `${colors.red[600]}88` : isDark ? colors.slate[700] : colors.slate[200]}`,
  };
}

function formatElapsed(ms: number | undefined | null): string {
  if (!ms) return '';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
